/**
 * useSubscriptionStatus Hook
 * ===========================
 * 
 * Combines auth + billing to determine subscription access permissions.
 * Returns computed subscription status info for guards and UI.
 */

import { useAuth } from '@/auth/AuthProvider';
import { useBillingCustomer } from './useBillingCustomer';
import type { SubscriptionStatusInfo } from '@/types/billing';

/**
 * Get current subscription status and permissions
 * 
 * Usage:
 * ```tsx
 * const { isActive, isProSubscription, isPastDue } = useSubscriptionStatus();
 * 
 * if (!isActive) {
 *   return <Redirect to="/settings/billing" />;
 * }
 * ```
 */
export function useSubscriptionStatus(): SubscriptionStatusInfo & { loading: boolean } {
  const { user } = useAuth();
  const { billingCustomer, loading } = useBillingCustomer(user?.uid);

  if (loading) {
    return {
      loading: true,
      isBillingInitialized: false,
      isActive: false,
      isPastDue: false,
      isProSubscription: false,
    };
  }

  if (!billingCustomer) {
    return {
      loading: false,
      isBillingInitialized: false,
      isActive: false,
      isPastDue: false,
      isProSubscription: false,
    };
  }

  const isActive = ['active', 'trialing'].includes(billingCustomer.subscriptionStatus);
  const isPastDue = billingCustomer.subscriptionStatus === 'pastDue';
  const isProSubscription = billingCustomer.subscriptionType === 'pro';

  return {
    loading: false,
    isBillingInitialized: true,
    isActive,
    isPastDue,
    subscriptionType: billingCustomer.subscriptionType,
    billingInterval: billingCustomer.billingInterval,
    isProSubscription,
    stripeCustomerId: billingCustomer.stripeCustomerId || undefined,
  };
}
